/**
 * catalogSearch — pure helpers for the billing product search, shared by the
 * hero SearchAddBar (and the legacy catalog grid). No React, no DB, no models,
 * so they stay trivially unit-testable.
 */

/** Minimal product shape needed to score a search match. */
export interface SearchableProduct {
  name: string;
  sku?: string | null;
  barcode?: string | null;
}

/**
 * Parse a leading/trailing quantity out of the search text so a cashier can type
 * "3 milk", "3*milk", "3x milk", or "milk*3" to add several at once. A bare
 * number (barcode/SKU) is left untouched.
 */
export function parseQtyQuery(raw: string): { qty: number; term: string } {
  const s = raw.trim();
  let m = s.match(/^(\d{1,4})\s*[*x]\s*(.+)$/i); // 3*term / 3x term
  if (m) return { qty: Math.max(1, parseInt(m[1], 10)), term: m[2].trim() };
  m = s.match(/^(.+?)\s*[*x]\s*(\d{1,4})$/i); // term*3 / term x3
  if (m) return { qty: Math.max(1, parseInt(m[2], 10)), term: m[1].trim() };
  m = s.match(/^(\d{1,4})\s+(\D.*)$/); // "3 term" — number, space, non-digit start
  if (m) return { qty: Math.max(1, parseInt(m[1], 10)), term: m[2].trim() };
  return { qty: 1, term: s };
}

/** Relevance score for a product against a lowercase query (lower = better). */
export function matchScore(p: SearchableProduct, q: string): number {
  const name = p.name.toLowerCase();
  const sku = (p.sku || '').toLowerCase();
  const bc = (p.barcode || '').toLowerCase();
  if (sku === q || bc === q) return 0; // exact code
  if (name === q) return 1; // exact name
  if (name.startsWith(q)) return 2; // name prefix
  if (name.split(/\s+/).some((w) => w.startsWith(q))) return 3; // word-start
  if (sku.startsWith(q) || bc.startsWith(q)) return 4; // code prefix
  if (name.includes(q)) return 5; // substring
  return 6; // matched only via a code substring
}
